import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { WalletProvider } from "@/contexts/WalletContext";
import { UserPreferencesProvider } from "@/contexts/UserPreferencesContext";
import { BlockchainEventProvider } from "@/contexts/BlockchainEventContext";
import { UserOnboarding } from "@/components/onboarding/UserOnboarding";
import { useOnboarding } from "@/hooks/use-onboarding";
import { AIAssistant } from "./components/chat/AIAssistant";
import { GlobalTradeHandler } from "./components/trading/GlobalTradeHandler";
import ErrorBoundary from "@/components/ErrorBoundary";
import Index from "./pages/Index";
import CoinDetail from "./pages/CoinDetail";
import CreateCoin from "./pages/CreateCoin";
import Trending from "./pages/Trending";
import NewCoins from "./pages/NewCoins";
import Leaderboard from "./pages/Leaderboard";
import Communities from "./pages/Communities";
import Streams from "./pages/Streams";
import Analytics from "./pages/Analytics";
import Settings from "./pages/Settings";
import Portfolio from "./pages/Portfolio";
import Sniper from "./pages/Sniper";
import CopyTrading from "./pages/CopyTrading";
import Whales from "./pages/Whales";
import Discover from "./pages/Discover";
import Admin from "./pages/Admin";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import Support from "./pages/Support";
import Terms from "./pages/Terms";
import API from "./pages/API";
import Docs from "./pages/Docs";
import StreamerDashboard from "./pages/StreamerDashboard";
import Marketplace from "./pages/Marketplace";
import CopyTradingSubscriptions from "./pages/CopyTradingSubscriptions";
import CommunityHub from "./pages/CommunityHub";
import Dashboard from "./pages/Dashboard";
import AISignals from "./pages/AISignals";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

function AppContent() {
  const { shouldShowOnboarding, completeOnboarding } = useOnboarding();

  return (
    <>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/discover" element={<Discover />} />
        <Route path="/coin/:id" element={<CoinDetail />} />
        <Route path="/create" element={<CreateCoin />} />
        <Route path="/trending" element={<Trending />} />
        <Route path="/new" element={<NewCoins />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/communities" element={<Communities />} />
        <Route path="/streams" element={<Streams />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/portfolio" element={<Portfolio />} />
        <Route path="/sniper" element={<Sniper />} />
        <Route path="/copy-trading" element={<CopyTrading />} />
        <Route path="/whales" element={<Whales />} />
        <Route path="/admin" element={<ProtectedRoute requireAdmin><Admin /></ProtectedRoute>} />
        <Route path="/support" element={<Support />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/api" element={<API />} />
        <Route path="/docs" element={<Docs />} />
        <Route path="/streamer" element={<StreamerDashboard />} />
        <Route path="/marketplace" element={<Marketplace />} />
        <Route path="/copy-subscriptions" element={<CopyTradingSubscriptions />} />
        <Route path="/community-hub" element={<CommunityHub />} />
        <Route path="/ai-signals" element={<AISignals />} />
        <Route path="*" element={<NotFound />} />
      </Routes>

      <UserOnboarding
        open={shouldShowOnboarding}
        onComplete={completeOnboarding}
      />
      <AIAssistant />
      <GlobalTradeHandler />
    </>
  );
}

const App = () => {
  useEffect(() => {
    document.body.classList.add('loaded');
  }, []);

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <WalletProvider>
            <BlockchainEventProvider>
              <UserPreferencesProvider>
                <TooltipProvider>
                  <Toaster />
                  <Sonner />
                  <BrowserRouter>
                    <AppContent />
                  </BrowserRouter>
                </TooltipProvider>
              </UserPreferencesProvider>
            </BlockchainEventProvider>
          </WalletProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
};

export default App;
